import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { LinearProgress } from "@mui/material"
import { GridColDef, GridRowSelectionModel } from "@mui/x-data-grid"
import moment from "moment"

import BasicButton from "@/components/button/BasicButton"
import Checkbox, { BaseCheckbox } from "@/components/form/checkbox/Checkbox"
import Selector from "@/components/form/selector/Selector"
import Table from "@/components/table/Table"
import Pagination from "@/components/table/Pagination"
import Chip from "@/components/chip/Chip"
import Icon from "@/components/icon/Icon"
import { colorTokens } from "@/components/colorTokens"
import RadioGroup from "@/components/form/radioGroup/RadioGroup"
import { createAccountStore } from "@/stores/account"
import { createGlobalStore } from "@/stores/global"
import { useFilteredActivitiesQuery } from "@/hooks/api/useActivityQuery"
import {
  useReportTemplateDetailQuery,
  useReportTemplateMetasQuery,
  useUpdateReportTemplateLastUsedMutation,
} from "@/hooks/api/useReportQuery"
import { useMetricList } from "@/hooks/api/useMetricQuery"
import {
  useUnitQuery,
  usePlayersQuery,
  useCalculatedMetricsQuery,
} from "@/hooks/api/useSettingQuery"
import { useTranslateMetricKey } from "@/hooks/metric/useTranslateKeyHook"
import { usePositionRank, usePositionGroupRank } from "@/hooks/usePositionRank"
import {
  readTrendGroupSummary,
  readDailySessionGroupSummary,
  readDailyActivityGroupSummary,
} from "@/app/api/report"
import { readSessionList } from "@/app/api/activity"
import { REPORT_TYPE, SECTION_TYPE, ACTIVITY_SESSION_TYPE } from "@/app/report/common/constants"
import { ActivityType } from "@/types/activity"
import { REPORT_STATUS_OBJECT } from "@/constants/report"
import { REPORT_DATE_FORMAT } from "@/constants/date"
import {
  buildDailyBatchCSVFiles,
  buildTrendBatchCSVFiles,
  downloadBatchCSV,
  StatsSectionSpec,
} from "@/app/report/common/export/batchCsv"
import { resolvePeriodDates } from "@/utils/filterPreset"
import { DEFAULT_PRESET_PAYLOAD } from "@/app/settings/report/filterPreset/constants"
import CsvDetailedFilters from "./CsvDetailedFilters"
import type { FilteredActivityItem } from "@/app/api/activity"
import type { UpsertFilterPresetPayload } from "@/types/filterPreset"
import type { StatsSettings, MetricMapType } from "@/app/report/common/type"
import type { MetricMeta } from "@/types/metric"

import "@/app/settings/report/filterPreset/filterPreset.scss"
import "@/app/report/components/filter/activitySelector/activitySelectorDialog.scss"
import "./csvExport.scss"

// 세부 필터 OFF(전체 조회) 시 사용하는 고정 종료 시각(2100-01-01 UTC). Date.now() 를 쓰면 매 렌더 값이 바뀌어 불필요한 refetch 발생.
const ALL_ACTIVITIES_END = 4102444800000

type SectionOption = { type: string; labelKey: string; fallback: string; supported: boolean }

// Stats 섹션은 템플릿 로드 시 Custom Table 로 대치되므로(#99) 선택지에서 뺀다 — 기존 Stats 템플릿도 Table 로 내려간다
const SECTION_OPTIONS_BY_REPORT: Record<string, SectionOption[]> = {
  [REPORT_TYPE.DAILY]: [
    { type: SECTION_TYPE.CUSTOM_TABLE, labelKey: "table", fallback: "Table", supported: true },
  ],
  [REPORT_TYPE.TREND]: [
    { type: SECTION_TYPE.CUSTOM_TABLE, labelKey: "table", fallback: "Table", supported: true },
  ],
}

export default function CSVExportPage() {
  const { t } = useTranslation()
  const setSnackBar = createGlobalStore((s) => s.setSnackBar)
  const teamProfileInfo = createAccountStore((s) => s.teamProfileInfo)
  const teamName = teamProfileInfo?.name ?? "Team"

  // --- 필터 상태 (프리셋 에디터와 동일한 모델) ---
  // 기간 기본값은 All time: 필터를 켠 직후에도 접힘 상태(전체 조회)와 목록이 달라지지 않게
  const [form, setForm] = useState<UpsertFilterPresetPayload>({
    ...DEFAULT_PRESET_PAYLOAD,
    periodType: "allTime",
  })
  const updateForm = (updates: Partial<UpsertFilterPresetPayload>) =>
    setForm((prev) => ({ ...prev, ...updates }))

  // 상세 필터 패널 펼침 = 필터 적용. 접힘(기본)이면 조회 범위 미적용(전체 액티비티 노출)
  // 패널 펼침(즉시, 버튼·애니메이션용)과 조회 전환(트랜지션 완료 후)을 분리 —
  // 수백 행 표 재렌더가 여닫는 애니메이션과 겹쳐 굼떠 보이지 않도록 전환을 미룬다
  const [filtersExpanded, setFiltersExpanded] = useState(false)
  const [detailFiltersEnabled, setDetailFiltersEnabled] = useState(false)

  // --- 메타 ---
  const { data: metricList } = useMetricList()
  const { data: selectedUnitList } = useUnitQuery()
  // Custom Table 요약 행(#98)의 커스텀 계산 메트릭 수식 재적용용
  const { data: calculatedMetrics } = useCalculatedMetricsQuery()
  const translateKey = useTranslateMetricKey()
  const positionRank = usePositionRank()
  const positionGroupRank = usePositionGroupRank()
  const { data: templates } = useReportTemplateMetasQuery({ reportType: form.reportType }, true)
  const { data: templateDetail } = useReportTemplateDetailQuery(form.idfTemplate ?? undefined)
  const { mutate: updateTemplateLastUsed } = useUpdateReportTemplateLastUsedMutation()

  // 리포트 필터와 동일한 기본 선택: 미선택이면 마지막 사용 템플릿(isLastUsed), 없으면 첫 번째
  useEffect(() => {
    if (templates && templates.length > 0 && !form.idfTemplate) {
      const lastUsedTemplate = templates.find((tpl) => tpl.isLastUsed)
      updateForm({ idfTemplate: lastUsedTemplate?.idfTemplate ?? templates[0].idfTemplate })
    }
  }, [templates, form.idfTemplate])

  const selectedTemplateObj = useMemo(() => {
    if (!form.idfTemplate || !templates) {
      return null
    }

    return templates.find((tpl) => tpl.idfTemplate === form.idfTemplate) ?? null
  }, [form.idfTemplate, templates])

  const metricMap: MetricMapType = useMemo(
    () =>
      (metricList ?? []).reduce(
        (map, metric) => map.set(metric.key, metric),
        new Map<string, MetricMeta>(),
      ),
    [metricList],
  )

  // --- 액티비티 목록 조회 ---
  // 리포트타입/템플릿 등 조회와 무관한 form 변경으로 refetch 되지 않도록, 실제 쿼리에 쓰는 필드에만 의존.
  const { data: players } = usePlayersQuery()
  const activityParams = useMemo(() => {
    // 세부 필터 OFF → 범위 제한 없이 전체 액티비티 (start 0 = 하한 없음, end 는 고정 상한)
    if (!detailFiltersEnabled) {
      return { start: 0, end: ALL_ACTIVITIES_END }
    }

    const isAllTime = form.periodType === "allTime"
    const { dateStart, dateFinish } = resolvePeriodDates(
      form.periodType,
      form.customDateStart,
      form.customDateEnd,
    )
    // 선수는 전체 선택 = 필터 미적용(파라미터 생략) — 액티비티 목록 필터 팝오버와 동일 규칙.
    // BE 의 idfPlayer 조건(activityData.idfPlayer IN)은 전원을 넘겨도 선수 매칭이 없는
    // 액티비티를 제외해 버려, 생략해야 미분석(0명) 액티비티가 유지된다
    const isAllPlayers = !!players?.length && form.selectedPlayerIds.length === players.length

    return {
      start: isAllTime ? 0 : moment(dateStart).startOf("day").valueOf(),
      end: isAllTime ? ALL_ACTIVITIES_END : moment(dateFinish).endOf("day").valueOf(),
      type: form.activityTypes.length ? form.activityTypes.join(",") : undefined,
      idfPlayer:
        form.selectedPlayerIds.length && !isAllPlayers
          ? form.selectedPlayerIds.join(",")
          : undefined,
      idfSeasonWeek: form.seasonWeekIds.length ? form.seasonWeekIds.join(",") : undefined,
      matchDay: form.matchDayTypes.length ? form.matchDayTypes.join(",") : undefined,
    }
  }, [
    detailFiltersEnabled,
    players,
    form.periodType,
    form.customDateStart,
    form.customDateEnd,
    form.activityTypes,
    form.selectedPlayerIds,
    form.seasonWeekIds,
    form.matchDayTypes,
  ])

  // 30초 캐시: 필터 켰다 꺼도(전체조회↔조건조회 왕복) 같은 조건은 리로드 없이 즉시 표시
  const { data: activities } = useFilteredActivitiesQuery(activityParams, 30_000)
  // ActivitySelectorDialog.newActivityList 와 동일: 분석 완료(ANALYZED)가 아니면 비활성 → 선택 불가
  const activityList = useMemo(
    () => (activities ?? []).map((a) => ({ ...a, isDisabled: a.status !== "ANALYZED" })),
    [activities],
  )

  const [selectedActivityIds, setSelectedActivityIds] = useState<number[]>([])
  const activityIdsKey = activityList.map((a) => a.idfActivity).join(",")
  useEffect(() => {
    // 기본 선택은 분석 완료 활동만 (다이얼로그 availableActivityList)
    setSelectedActivityIds(activityList.filter((a) => !a.isDisabled).map((a) => a.idfActivity))
  }, [activityIdsKey])

  const rowSelectionModel: GridRowSelectionModel = useMemo(() => {
    const visible = new Set(activityList.map((a) => a.idfActivity))

    return { type: "include", ids: new Set(selectedActivityIds.filter((id) => visible.has(id))) }
  }, [selectedActivityIds, activityList])

  // Trend > Select Activity 다이얼로그(ActivitySelectorDialog)와 동일한 컬럼 구성
  const activityColumns: GridColDef[] = [
    {
      field: "dateStart",
      renderHeader: () => t("date"),
      renderCell: (params) => moment(params.row.dateStart).format(REPORT_DATE_FORMAT),
      width: 120,
      sortable: false,
    },
    {
      field: "name",
      renderHeader: () => t("activityName"),
      renderCell: (params) => (
        <div className="activity-name-cell">
          <p>{params.row.name}</p>
          {params.row.status !== "ANALYZED" && (
            <Chip type="default-small" label={t(REPORT_STATUS_OBJECT[params.row.status])} />
          )}
        </div>
      ),
      flex: 1,
      minWidth: 200,
      sortable: false,
    },
    {
      field: "playerCount",
      renderHeader: () => t("player"),
      width: 80,
      sortable: false,
    },
    {
      field: "type",
      renderHeader: () => t("type"),
      renderCell: (params) => t(ActivityType[params.row.type as keyof typeof ActivityType]),
      width: 140,
      sortable: false,
    },
  ]

  // --- 섹션 선택 ---
  const sectionOptions = useMemo(() => {
    const opts = SECTION_OPTIONS_BY_REPORT[form.reportType] ?? []
    const content = templateDetail?.content ?? []

    return opts.map((opt) => ({
      ...opt,
      available: opt.supported && content.some((s) => s.active && s.section === opt.type),
    }))
  }, [form.reportType, templateDetail])

  const [selectedSectionTypes, setSelectedSectionTypes] = useState<string[]>([])
  const availableSectionKey = sectionOptions
    .filter((o) => o.available)
    .map((o) => o.type)
    .join(",")
  useEffect(() => {
    setSelectedSectionTypes(availableSectionKey ? availableSectionKey.split(",") : [])
  }, [availableSectionKey])

  // --- Daily 집계 모드 (Activity / All Sessions). 기본 All Sessions ---
  const [dailySessionType, setDailySessionType] = useState<string>(ACTIVITY_SESSION_TYPE.SESSION)

  // --- 다운로드 ---
  const [isDownloading, setIsDownloading] = useState(false)
  const [progress, setProgress] = useState({ done: 0, total: 0 })

  const selectedActivities = useMemo(
    () => activityList.filter((a) => selectedActivityIds.includes(a.idfActivity)),
    [activityList, selectedActivityIds],
  )

  // Trend 표 섹션(customTable — Stats 는 템플릿 로드 시 customTable 로 대치됨 #99) → 리포트와 동일 방식으로 CSV
  const trendSections: StatsSectionSpec[] = useMemo(
    () =>
      (templateDetail?.content ?? [])
        .filter(
          (s) =>
            s.active &&
            s.section === SECTION_TYPE.CUSTOM_TABLE &&
            selectedSectionTypes.includes(s.section),
        )
        .map((s) => ({
          sectionType: s.section,
          title: (s.settings as StatsSettings).title,
          statsSettings: s.settings as StatsSettings,
        })),
    [templateDetail, selectedSectionTypes],
  )

  const canDownload =
    !!form.idfTemplate &&
    selectedActivities.length > 0 &&
    selectedSectionTypes.length > 0 &&
    !isDownloading

  // Daily 표 섹션(customTable) → 리포트와 동일 방식으로 CSV
  const dailySections: StatsSectionSpec[] = useMemo(
    () =>
      (templateDetail?.content ?? [])
        .filter(
          (s) =>
            s.active &&
            s.section === SECTION_TYPE.CUSTOM_TABLE &&
            selectedSectionTypes.includes(s.section),
        )
        .map((s) => ({
          sectionType: s.section,
          title: (s.settings as StatsSettings).title,
          statsSettings: s.settings as StatsSettings,
        })),
    [templateDetail, selectedSectionTypes],
  )

  // Custom Table: 리포트 화면과 동일한 그룹 집계(/summary/trend/group). groupKey 는 섹션 settings 에서.
  const fetchGroupSummary = async (idfActivityList: number[], settings: StatsSettings) => {
    const { success, response } = await readTrendGroupSummary({
      idfActivityList,
      idfPlayerList: form.selectedPlayerIds,
      groupKey1: settings.groupKey1 || "",
      groupKey2: settings.groupKey2,
    })

    return success && response ? response : []
  }

  // Daily Custom Table: 화면과 같은 기준 — All Sessions 모드는 액티비티의 전체 세션으로 세션 단위 집계,
  // Activity 모드(또는 세션 없는 액티비티)는 액티비티 집계 (#99-1)
  const fetchDailyGroupSummary = async (idfActivity: number, settings: StatsSettings) => {
    if (dailySessionType !== ACTIVITY_SESSION_TYPE.ACTIVITY) {
      const sessRes = await readSessionList({ idfActivity })
      const sessionIds =
        sessRes.success && sessRes.response ? sessRes.response.map((s) => s.idfSession) : []
      if (sessionIds.length) {
        const { success, response } = await readDailySessionGroupSummary({
          idfSessionList: sessionIds,
          idfPlayerList: form.selectedPlayerIds,
          groupKey1: settings.groupKey1 || "",
          groupKey2: settings.groupKey2,
        })

        return success && response ? response : []
      }
    }

    // 액티비티 모드(또는 세션 없는 액티비티)는 액티비티 전체 기준으로 받는다 — 화면과 같은 기준.
    // trend/group 은 세션 요약을 읽어 세션 구간만 합산하므로, 세션 밖 데이터와 그 구간에만 뛴
    // 선수가 빠진다. 화면 Custom Table 은 #104 에서 이미 바꿨는데 CSV 만 남아 있었다 (ERR-041)
    const { success, response } = await readDailyActivityGroupSummary({
      idfActivityList: [idfActivity],
      idfPlayerList: form.selectedPlayerIds,
      groupKey1: settings.groupKey1 || "",
      groupKey2: settings.groupKey2,
    })

    return success && response ? response : []
  }

  const handleDownload = async () => {
    const isDaily = form.reportType === REPORT_TYPE.DAILY
    if (!selectedActivities.length || (isDaily ? !dailySections.length : !trendSections.length)) {
      return
    }

    setIsDownloading(true)
    setProgress({ done: 0, total: isDaily ? selectedActivities.length : trendSections.length })

    if (form.idfTemplate) {
      updateTemplateLastUsed({
        idfReportTemplate: form.idfTemplate,
        reportType: isDaily ? "daily" : "trend",
      })
    }

    const idfActivityList = selectedActivities.map((a) => a.idfActivity)

    try {
      const files = isDaily
        ? await buildDailyBatchCSVFiles({
            activities: selectedActivities.map((a) => ({
              idfActivity: a.idfActivity,
              name: a.name,
            })),
            sections: dailySections,
            fetchGroupSummary: fetchDailyGroupSummary,
            positionRankFns: { positionRank, positionGroupRank },
            metricMap,
            selectedUnitList,
            calculatedMetrics: calculatedMetrics ?? undefined,
            t,
            translateKey,
            onProgress: (done, total) => setProgress({ done, total }),
          })
        : await buildTrendBatchCSVFiles({
            sections: trendSections,
            teamName,
            fetchGroupSummary: (settings) => fetchGroupSummary(idfActivityList, settings),
            positionRankFns: { positionRank, positionGroupRank },
            metricMap,
            selectedUnitList,
            calculatedMetrics: calculatedMetrics ?? undefined,
            t,
            translateKey,
            onProgress: (done, total) => setProgress({ done, total }),
          })

      if (!files.length) {
        setSnackBar({
          open: true,
          message: t("csvExportEmpty", "No data to export."),
          severity: "warning",
        })

        return
      }

      await downloadBatchCSV(files, `csv-export_${teamName}`)
    } catch {
      setSnackBar({
        open: true,
        message: t("csvExportFailed", "Failed to export CSV."),
        severity: "error",
      })
    } finally {
      setIsDownloading(false)
    }
  }

  const toggleSection = (type: string) => {
    setSelectedSectionTypes((prev) =>
      prev.includes(type) ? prev.filter((x) => x !== type) : [...prev, type],
    )
  }

  return (
    <div className="CsvExportPageCont">
      <div className="csv-export-header">
        <h3>{t("csvBulkDownload", "CSV Bulk Download")}</h3>
      </div>

      <div className="FilterPresetEditorCont csv-export-body">
        {/* Report Type / Report Template */}
        <div className="FilterPresetEditor__grid FilterPresetEditor__grid--2col">
          <div className="FilterPresetEditor__field">
            <label className="FilterPresetEditor__label">{t("reportType", "Report Type")}</label>
            <RadioGroup
              direction="row"
              value={form.reportType}
              items={[
                { value: REPORT_TYPE.DAILY, label: t("activityReport", "Activity Report") },
                { value: REPORT_TYPE.TREND, label: t("trendReport", "Trend Report") },
              ]}
              onChange={(e) =>
                updateForm({ reportType: e.target.value as REPORT_TYPE, idfTemplate: null })
              }
            />
          </div>
          <div className="FilterPresetEditor__field">
            <label className="FilterPresetEditor__label">
              {t("reportTemplate", "Report Template")}
            </label>
            <Selector
              type="autocomplete"
              options={templates ?? []}
              value={selectedTemplateObj}
              onChange={(_e: unknown, tpl: { idfTemplate: number } | null) =>
                updateForm({ idfTemplate: tpl?.idfTemplate ?? null })
              }
              labelKey="name"
              getOptionKey={(opt: any) => String(opt.idfTemplate)}
              placeholder={t("selectTemplate", "Select template") as string}
              prependIcon={
                <Icon type="table-line" size={16} color={colorTokens.field["ico-base"]} />
              }
              fullWidth
            />
          </div>
        </div>

        <div className="FilterPresetEditor__divider" />

        {/* 상세 필터 + 액티비티 표 */}
        <div className="csv-export-activity-area">
          <CsvDetailedFilters
            form={form}
            updateForm={updateForm}
            expanded={filtersExpanded}
            onToggleExpanded={setFiltersExpanded}
            onTransitionEnd={setDetailFiltersEnabled}
            selectedActivityIds={selectedActivityIds}
          />

          <div className="csv-export-activity-label-row">
            <p>{t("activity", "Activity")}</p>
            <Chip type="number" label={`${selectedActivities.length}/${activityList.length}`} />
          </div>
          <div className="PlayerListSelector__tableWrap">
            <Table
              columns={activityColumns}
              rows={activityList}
              getRowId={(row) => (row as FilteredActivityItem).idfActivity}
              isRowSelectable={(params) => !params.row.isDisabled}
              rowSelectionModel={rowSelectionModel}
              onRowSelectionModelChange={(model) => {
                setSelectedActivityIds(
                  activityList.map((a) => a.idfActivity).filter((id) => model.ids.has(id)),
                )
              }}
              selectionType="checkbox"
              selectionElement={(checkboxProps) => (
                <BaseCheckbox
                  checked={checkboxProps.checked}
                  indeterminate={checkboxProps.indeterminate}
                  onChange={checkboxProps.onChange}
                  disabled={checkboxProps.disabled}
                />
              )}
              columnHeaderHeight={41}
              rowHeight={44}
              disableColumnResize
              disableColumnMenu
              disableColumnSorting
              slots={{ pagination: Pagination }}
              sx={{ width: "100%", height: "100%" }}
            />
          </div>
        </div>

        <div className="FilterPresetEditor__divider" />

        {/* Aggregation / Sections to Export */}
        <div className="FilterPresetEditor__grid FilterPresetEditor__grid--2col">
          {form.reportType === REPORT_TYPE.DAILY && (
            <div className="FilterPresetEditor__field">
              <label className="FilterPresetEditor__label">{t("aggregation", "Aggregation")}</label>
              <RadioGroup
                className="FilterPresetEditor__options"
                direction="row"
                value={dailySessionType}
                items={[
                  {
                    value: ACTIVITY_SESSION_TYPE.SESSION,
                    label: t("allSessions", "All Sessions"),
                  },
                  { value: ACTIVITY_SESSION_TYPE.ACTIVITY, label: t("activity", "Activity") },
                ]}
                onChange={(e) => setDailySessionType(e.target.value)}
              />
            </div>
          )}
          <div className="FilterPresetEditor__field">
            <label className="FilterPresetEditor__label">
              {t("exportSections", "Sections to Export")}
            </label>
            <div className="FilterPresetEditor__options">
              {sectionOptions.map((opt) => (
                <Checkbox
                  key={opt.type}
                  checked={opt.available && selectedSectionTypes.includes(opt.type)}
                  disabled={!opt.available}
                  size="medium"
                  onCheckboxChange={() => toggleSection(opt.type)}
                  label={t(opt.labelKey, opt.fallback)}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Download */}
        <div className="csv-export-download-row">
          {isDownloading && progress.total > 0 && (
            <div className="csv-export-progress">
              <div className="csv-export-progress-labels">
                <span>
                  {t("downloading", "Downloading...")} {progress.done}/{progress.total}
                </span>
                <span>{Math.round((progress.done / progress.total) * 100)}%</span>
              </div>
              <LinearProgress
                variant="determinate"
                value={(progress.done / progress.total) * 100}
              />
            </div>
          )}
          <BasicButton
            type="primary-contained"
            size="lg"
            onClick={handleDownload}
            disabled={!canDownload}
            isLoading={isDownloading}>
            {t("download", "Download")}
          </BasicButton>
        </div>
      </div>
    </div>
  )
}
